import { useEffect } from 'react'
import { Outlet, redirect, useNavigate, useRouter, useRouterState, createFileRoute } from '@tanstack/react-router'
import { Shell } from '@/app/Shell'
import { authReady, useAuthStore } from '@/stores/auth.store'
import { useAgentStore } from '@/stores/agent.store'
import {
  AccountProvisioningGate,
  WorkspaceGate,
} from '@/features/account/components/workspace-gate'
import { CompatibilityGate } from '@/features/compatibility/components/compatibility-gate'
import { CreditBanners } from '@/components/ui/credit-banners/credit-banners'
import { CreditHydrator } from '@/components/ui/credit-banners/credit-hydrator'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { useHandoffConsumer } from '@/features/handoff/useHandoffConsumer'
import { WorkspaceBootLoader } from '@/features/account/components/workspace-boot-loader'
import { HostedPreviewBootstrap } from '@/features/account/components/hosted-preview-bootstrap'
import { RoutePendingScreen } from '@/components/RoutePendingScreen/RoutePendingScreen'
import { useProjectEntryTransitionStore } from '@/features/dashboard/project-entry-transition.store'
import { loginSearchFor } from '@/features/auth/signup/project-login-target'

/**
 * Pathless layout route for authenticated pages.
 *
 * Wraps child routes with the three-panel Shell. Routes outside _app/
 * (e.g., /login) render without the shell chrome.
 *
 * The beforeLoad guard awaits Firebase auth initialization, then redirects
 * unauthenticated users to /login. This prevents the "login flash" on
 * page refresh while Firebase rehydrates the session from IndexedDB.
 *
 * The component also reactively watches auth state so that signing out
 * immediately redirects to /login without a full page reload.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    await authReady
    if (!useAuthStore.getState().user) {
      // Preserve the intended destination so a successful sign-in returns the
      // user to where they were headed, not to the dashboard.
      throw redirect({
        to: '/login',
        search: loginSearchFor(location.pathname, location.searchStr ?? ''),
      })
    }
  },
  component: AppLayout,
})

function AppLayout() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const router = useRouter()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const projectEntryTarget = useProjectEntryTransitionStore(
    (state) => state.targetProjectId,
  )

  // Notify users to refresh when a new bundle has been deployed.
  useVersionCheck()

  // Consume a pending cross-app prompt handoff (if any). The hook is a no-op
  // when sessionStorage is empty; when present it fires `launch('bru', …)`
  // exactly once and returns `true` while the create-project flow is in
  // flight, so we can show a spinner instead of flashing the dashboard.
  const isHandoffPending = useHandoffConsumer()
  const hideWorkspace =
    pathname === '/' ||
    pathname === '/dashboard' ||
    pathname === '/projects' ||
    pathname === '/agents' ||
    pathname === '/brands' ||
    pathname === '/forbidden' ||
    pathname === '/buying-plan'

  useEffect(() => {
    if (user) return
    // Only a settled sign-out reaches here (the auth store ignores another tab's
    // momentary null). Keep where the user was, so signing back in returns them.
    const { pathname: here, searchStr } = router.state.location
    void navigate({ to: '/login', search: loginSearchFor(here, searchStr), replace: true })
  }, [user, navigate, router])

  useEffect(() => {
    if (hideWorkspace) {
      useAgentStore.getState().abandonDraftProject()
    }
  }, [hideWorkspace, pathname])

  if (!user) return null

  if (isHandoffPending) {
    return <WorkspaceBootLoader message="Preparing your dashboard" />
  }

  if (projectEntryTarget) {
    return <RoutePendingScreen />
  }

  // WorkspaceGate wraps the Shell, not just the Outlet. Shell renders the
  // Sidebar and ChatPanel as siblings of `children` — if the gate only
  // swapped the Outlet, those would still render and the user would see
  // the workspace chrome next to the block. Gating at Shell level gives
  // a true full-screen takeover when the caller has no workspace.
  //
  // AccountProvisioningGate is the only authenticated request allowed before
  // CompatibilityGate. The idempotent /v2/me bootstrap must exist before the
  // compatibility endpoint can authorize a never-seen Firebase user. A stale
  // build still cannot issue workspace reads because WorkspaceGate remains below
  // CompatibilityGate.
  return (
    <AccountProvisioningGate>
      <CompatibilityGate>
        <HostedPreviewBootstrap>
          <WorkspaceGate>
            <Shell hideWorkspace={hideWorkspace}>
              <Outlet />
              <CreditHydrator />
              <CreditBanners.Bottom />
              <CreditBanners.Modals />
            </Shell>
          </WorkspaceGate>
        </HostedPreviewBootstrap>
      </CompatibilityGate>
    </AccountProvisioningGate>
  )
}
